import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import { Collapse } from '@mui/material';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import { Close, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAuthDataOrganisationId } from 'selectors/auth';
import { downloadService } from 'services';
import { ScoringToolSteps } from 'shared/constants/esg/scoringToolSteps';
import { useNotifications } from 'shared/hooks';
import { BaseProps } from 'shared/types/base';
import { portfolioUploadSchema } from 'shared/validations';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { theme } from 'theme';
import { DialogTitleWithClose, Loader } from 'ui';
import { uploadCustomCompanies } from 'services/esg';
import { formatNumber } from 'shared/functions';

const useStyles = makeStyles({
  downloadTemplateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  downloadButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  input: {
    display: 'none',
  },
  uploadContainer: {
    padding: '2px 4px',
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  fileUploadText: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  summaryContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  checkIcon: {
    fontSize: 80,
  },
  selectButton: {
    width: 150,
    height: 100,
    textTransform: 'inherit',
    border: 'none',
    color: theme.palette.text.primary,
    boxShadow: '0 4px 4px 2px rgba(155, 155, 155, 0.3)',
    padding: '0 35px',
  },
  selectTitle: {
    fontSize: 15,
  },
});

type ESGScoringToolModalProps = {
  open: boolean;
  onClose: () => void;
  onUploaded?: (refresh: boolean, reason: string) => void;
  isPortfolioManager?: boolean;
  portfolioId?: string;
  selectedIds?: string[];
} & BaseProps;

type Data = {
  errors: { line: number; message: string }[];
  successfullyParsed: number;
};

const ESGScoringToolModal: FC<ESGScoringToolModalProps> = ({
  open,
  onClose,
  onUploaded,
  isPortfolioManager,
  portfolioId,
  selectedIds,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { notifyError } = useNotifications();

  const organisationId = useSelector(getAuthDataOrganisationId);

  const [fileUploadInputRef, setFileUploadInputRef] = useState<any>(null);

  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState<Data>({ errors: [], successfullyParsed: -1 });
  const [error, setError] = useState('');
  const [step, setStep] = useState(ScoringToolSteps.UPLOAD);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (error) {
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const errorsNumber = data?.errors?.length;
  const total = (errorsNumber ?? 0) + (data?.successfullyParsed ?? 0);

  const sampleTemplateUrl = '/esg/api/portfolio-not-found-isins-template/';

  const fileUploadInputRefChanged = (element: any) => setFileUploadInputRef(element);

  const handleStepSubmit = async (payload: any) => {
    if (step === ScoringToolSteps.UPLOAD) {
      setIsPending(true);
      try {
        const uploadData = await uploadCustomCompanies({
          companiesFile: payload && payload.fileUploadInput,
          organisationId,
          isPortfolioManager,
          portfolioId,
        });
        setData({ errors: uploadData.errors, successfullyParsed: uploadData.successfully_parsed });

        setStep(ScoringToolSteps.FINISH);

        if (onUploaded) {
          onUploaded(true, refreshReasons.UPDATED);
        }
      } catch (e) {
        setError((e as Error).message);
      }
      setIsPending(false);
    }
  };

  const downloadErrorHandler = () => {
    notifyError(t('ESG.FAILED_TO_DOWNLOAD_SAMPLE_TEMPLATE'));
  };

  const closeModal = () => {
    setStep(ScoringToolSteps.UPLOAD);
    onClose();
  };

  const handleDownloadTemplateClick = () =>
    downloadService({
      path: sampleTemplateUrl,
      name: 'companies-template.xlsx',
      onError: downloadErrorHandler,
      options: {
        body: JSON.stringify(selectedIds),
        method: 'POST',
      },
    });

  const updateUploadBoxData = (
    setFieldValueFunc: (field: string, value: any) => void,
    file: File | null,
    text: string,
  ) => {
    if (!file && fileUploadInputRef) {
      fileUploadInputRef.value = null;
    }
    setFieldValueFunc('fileUploadInput', file);
    setFieldValueFunc('fileUploadText', text);
  };

  const toggleErrorsList = () => {
    setShowErrors(!showErrors);
  };

  const stepUploadInitialValues = {
    description: '',
    origin: window.location.href,
    fileUploadText: '',
    fileUploadInput: null,
  };

  const stepUpload = (
    <Formik
      key={ScoringToolSteps.UPLOAD}
      initialValues={stepUploadInitialValues}
      onSubmit={payload => handleStepSubmit(payload)}
      validationSchema={portfolioUploadSchema(['csv', 'xls', 'xlsx'])}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('ESG.SCORING_TOOL_DESC_1')}</Typography>
              </Box>
              <Box mb={2}>
                <Typography>{t('ESG.SCORING_TOOL_DESC_2')}</Typography>
              </Box>
              <Divider />
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography>{`${t('File format')}: .csv, .xls, .xlsx`}</Typography>
                  <Typography>{`${t(
                    'Column heading',
                  )}: ISIN, COMPANY NAME, COUNTRY ISO 3 CODE, NACE INDUSTRY CODE`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={handleDownloadTemplateClick}
                    variant="outlined"
                    disabled={isPending}
                  >
                    {t('DOWNLOAD_TEMPLATE')}
                  </Button>
                </Box>
              </div>
              <Divider />
              <ErrorMessage name="fileUploadInput">
                {message => (
                  <Box mt={2}>
                    <Typography color="error">{message}</Typography>
                  </Box>
                )}
              </ErrorMessage>
              <Paper className={classes.uploadContainer}>
                <input
                  className={classes.input}
                  id="file-upload"
                  name="file-upload"
                  ref={fileUploadInputRefChanged}
                  type="file"
                  onChange={event => {
                    updateUploadBoxData(
                      setFieldValue,
                      event.currentTarget.files ? event.currentTarget.files[0] : null,
                      get(event.currentTarget.files, '[0].name', ''),
                    );
                  }}
                />
                <Field name="fileUploadText">
                  {({ field }: any) => (
                    <InputBase
                      {...field}
                      className={classes.fileUploadText}
                      placeholder={t('UPLOAD.NO_FILE_SELECTED')}
                      error={errors.fileUploadInput && touched.fileUploadText}
                      readOnly
                    />
                  )}
                </Field>
                <IconButton
                  type="submit"
                  disabled={!fileUploadInputRef || !fileUploadInputRef?.value}
                  onClick={() => {
                    updateUploadBoxData(setFieldValue, null, '');
                  }}
                  size="large">
                  <DeleteIcon />
                </IconButton>
                <label htmlFor="file-upload">
                  <Button variant="contained" color="primary" component="span">
                    {t('UPLOAD.BROWSE')}
                  </Button>
                </label>
              </Paper>
            </DialogContent>
            <DialogActions>
              <div style={{ flex: '1 0 0' }} />
              <Button variant="outlined" onClick={closeModal}>
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || !get(values, 'fileUploadInput')}
              >
                {t('Next')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepFinish = (
    <>
      <DialogContent className={classes.textCenter}>
        {get(data, 'successfullyParsed', 0) ? (
          <Typography variant="h6" className={classes.textCenter}>
            <Box
              width={140}
              height={140}
              borderRadius="50%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginY={2}
              marginX="auto"
              boxShadow={theme.customShadows[0]}
              bgcolor={theme.palette.custom.verySoftCyan}
            >
              <CheckIcon className={classes.checkIcon} />
            </Box>
            <Box mb={2} className={classes.summaryContent}>
              <Typography color="primary" variant="h3">
                {data?.successfullyParsed ?? 0}
              </Typography>
              <Box p={2}>
                <Typography variant="h6">{t('out of')}</Typography>
              </Box>
              <Typography variant="h3">{formatNumber(total)}</Typography>
            </Box>
            <Box p={2} mb={2}>
              <Typography variant="body1" className={classes.textCenter}>
                {t('ESG.SCORING_TOOL_SUCCESS')}
              </Typography>
            </Box>
          </Typography>
        ) : (
          <Typography variant="h6" className={classes.textCenter}>
            <Collapse in={!showErrors} timeout="auto" unmountOnExit>
              <Box
                width={140}
                height={140}
                borderRadius="50%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginY={2}
                marginX="auto"
                boxShadow={theme.customShadows[0]}
                bgcolor={theme.palette.custom.verySoftPink}
              >
                <Close className={classes.checkIcon} />
              </Box>
            </Collapse>
          </Typography>
        )}

        {data?.errors?.length ? (
          <>
            <Typography className={classes.textCenter}>
              <Button color="primary" onClick={toggleErrorsList}>
                {t('ERRORS')} ({data.errors.length}){' '}
                {showErrors ? <ArrowDropUp /> : <ArrowDropDown />}
              </Button>
            </Typography>
            <Collapse in={showErrors} timeout="auto" unmountOnExit>
              <Paper>
                <ul>
                  {data.errors.map((item: { line: number; message: string }, i) => {
                    // eslint-disable-next-line react/no-array-index-key
                    return <li key={i}>{item.message}</li>;
                  })}
                </ul>
              </Paper>
            </Collapse>
          </>
        ) : (
          undefined
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" disabled={isPending} onClick={closeModal}>
          {t('Done')}
        </Button>
      </DialogActions>
    </>
  );

  const stepsMapping: Record<ScoringToolSteps, JSX.Element> = {
    [ScoringToolSteps.UPLOAD]: stepUpload,
    [ScoringToolSteps.FINISH]: stepFinish,
  };

  return (
    <>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitleWithClose onClose={closeModal}>{t('ESG.SCORING_TOOL')}</DialogTitleWithClose>
        {stepsMapping[step]}
        {isPending && <Loader cover />}
      </Dialog>
    </>
  );
};

export default ESGScoringToolModal;
